import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { AlertTriangle, CheckCircle, Loader2, UserCircle } from 'lucide-react';

const ImpersonateLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const userDataParam = searchParams.get('user');
    const redirectTo = searchParams.get('redirect') || '/';

    if (!token) {
      setStatus('error');
      setMessage('Kein Token gefunden.');
      return;
    }

    try {
      // Parse user data
      const user = userDataParam ? JSON.parse(decodeURIComponent(userDataParam)) : null;
      setUserData(user);

      if (user && token) {
        // Use setAuth to properly store the auth state
        setAuth(user, token);
        
        setStatus('success');
        setMessage(`Eingeloggt als ${user.first_name} ${user.last_name}`);

        // Redirect after a short delay
        setTimeout(() => {
          navigate(redirectTo, { replace: true });
        }, 2000);
      } else {
        setStatus('error');
        setMessage('Benutzerdaten fehlen.');
      }

    } catch (error) {
      console.error('Impersonate login error:', error);
      setStatus('error');
      setMessage('Fehler beim Einloggen. Token möglicherweise ungültig.');
    }
  }, [searchParams, navigate, setAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#D4C4B0' }}>
      <div className="bg-white p-8 rounded-none shadow-lg max-w-md w-full mx-4 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="h-16 w-16 mx-auto mb-4 text-amber-600 animate-spin" />
            <h1 className="text-xl font-bold mb-2">Anmeldung läuft...</h1>
            <p className="text-gray-600">Bitte warten Sie einen Moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-600" />
            <h1 className="text-xl font-bold mb-2 text-green-700">Erfolgreich!</h1>
            <p className="text-gray-600 mb-4">{message}</p>
            {userData && (
              <div className="bg-gray-50 p-4 rounded-none border mb-4">
                <UserCircle className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p className="font-medium">{userData.first_name} {userData.last_name}</p>
                <p className="text-sm text-gray-500">{userData.email}</p>
                <span className={`inline-block mt-2 text-xs px-2 py-1 ${
                  userData.role === 'vendor' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {userData.role === 'vendor' ? 'Verkäufer' : 'Kunde'}
                </span>
              </div>
            )}
            <p className="text-sm text-gray-500">Sie werden weitergeleitet...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-red-500" />
            <h1 className="text-xl font-bold mb-2 text-red-700">Fehler</h1>
            <p className="text-gray-600 mb-4">{message}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-amber-600 text-white px-4 py-2 rounded-none hover:bg-amber-700"
            >
              Zur Startseite
            </button>
          </>
        )}

        <div className="mt-6 pt-4 border-t text-xs text-gray-400">
          <p>Admin-Vorschau-Modus</p>
          <p>Diese Session läuft in 1 Stunde ab.</p>
        </div>
      </div>
    </div>
  );
};

export default ImpersonateLogin;
